import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';
import {
  Mail, Clock, Building2, Briefcase, HelpCircle,
  Send, Loader2, CheckCircle, AlertCircle, Stethoscope
} from 'lucide-react';
import Icon3D from '../components/Icon3D';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const { tenant } = useTenant();

  const tenantName = tenant?.name || 'MedMCQ';
  const supportEmail = tenant?.support_email || 'support@medmcq.com.au';
  const enterpriseEmail = 'enterprise@medmcq.com.au';

  useEffect(() => {
    document.title = `Contact | ${tenantName}`;
  }, [tenantName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        subject: formData.subject || 'Contact Form Submission',
        message: formData.message
      });

      setResult({ success: true, message: response.data.message || 'Message sent successfully!' });
      setFormData({ firstName: '', lastName: '', email: '', subject: '', message: '' });
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to send message. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const subjectOptions = [
    'General Inquiry',
    'Technical Support',
    'Billing Question',
    'Feature Request',
    'Bug Report',
    'Partnership Opportunity',
    'Other'
  ];

  const faqItems = [
    { question: 'How do I cancel my subscription?', answer: 'You can cancel anytime from your subscription settings.' },
    { question: 'Can I import my own questions?', answer: 'Yes! Use our Import feature to upload your question banks.' },
    { question: 'Is there a student discount?', answer: 'All medical students get access to our free tier.' },
    { question: 'How does the AI assistant work?', answer: 'Our AI generates practice questions from your study materials.' },
  ];

  return (
    <Layout>
      {/* Hero Section - Dark Navy */}
      <div className="py-8 md:py-16" style={{ backgroundColor: '#1a2333' }}>
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-4">Get in Touch</h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#E0E0E0' }}>
            Have questions about {tenantName}? We're here to help you succeed in your medical studies.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-5 gap-4 md:gap-8">
          {/* Left Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email Us */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Icon3D icon={Mail} colour="turquoise" size="md" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">Email Us</h3>
                <p className="text-slate-500 text-sm mb-1">For general inquiries</p>
                <a href={`mailto:${supportEmail}`} className="text-cyan-600 font-medium hover:underline">
                  {supportEmail}
                </a>
              </div>
            </div>

            {/* Response Time */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Icon3D icon={Clock} colour="blue" size="md" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">Response Time</h3>
                <p className="text-slate-500 text-sm">
                  We typically respond within 24 hours during business days
                </p>
              </div>
            </div>

            {/* For Institutions */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Icon3D icon={Building2} colour="purple" size="md" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">For Institutions</h3>
                <p className="text-slate-500 text-sm mb-1">Interested in bulk licensing?</p>
                <a href={`mailto:${enterpriseEmail}`} className="text-cyan-600 font-medium hover:underline">
                  {enterpriseEmail}
                </a>
              </div>
            </div>

            {/* Careers */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Icon3D icon={Briefcase} colour="amber" size="md" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">Careers</h3>
                <p className="text-slate-500 text-sm mb-1">Join our team</p>
                <a href="/jobs" className="text-cyan-600 font-medium hover:underline">
                  View open positions
                </a>
              </div>
            </div>

            {/* Common Questions */}
            <div className="bg-slate-50 rounded-2xl p-6 mt-8" style={{ boxShadow: 'var(--shadow-card)' }}>
              <h3 className="font-bold text-slate-900 text-lg mb-4">Common Questions</h3>
              <div className="space-y-4">
                {faqItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5"><Icon3D icon={HelpCircle} colour="turquoise" size="sm" /></div>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{item.question}</p>
                      <p className="text-slate-500 text-xs mt-1">{item.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-8" style={{ boxShadow: 'var(--shadow-card)' }}>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h2>

              {/* Result Message */}
              {result && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
                  result.success
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {result.success ? (
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  <span>{result.message}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">First Name</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                      placeholder="John"
                      required
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                      placeholder="Smith"
                      required
                      disabled={submitting}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                    placeholder="john@example.com"
                    required
                    disabled={submitting}
                  />
                </div>

                {/* Subject Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all appearance-none bg-white"
                    disabled={submitting}
                  >
                    <option value="">Select a topic...</option>
                    {subjectOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all resize-none"
                    placeholder="How can we help you?"
                    required
                    disabled={submitting}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 relative overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin relative z-10" />
                      <span className="relative z-10">Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 relative z-10" />
                      <span className="relative z-10">Send Message</span>
                    </>
                  )}
                </button>

                {/* Privacy Note */}
                <p className="text-xs text-slate-500 text-center">
                  By submitting this form, you agree to our{' '}
                  <a href="#" className="text-cyan-600 hover:underline">Privacy Policy</a>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Dark Blue Footer Section */}
      <div className="py-16" style={{ backgroundColor: '#1a2333' }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <Icon3D icon={Stethoscope} colour="turquoise" size="sm" />
                <span className="text-xl font-bold">
                  <span className="text-slate-400">Med</span>
                  <span className="text-cyan-400">MCQ</span>
                </span>
              </div>
              <p className="text-slate-400 text-sm">
                Empowering medical students with the tools they need to succeed in their exams and careers.
              </p>
            </div>

            {/* Product Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/questions" className="text-slate-400 hover:text-white transition-colors">Question Bank</a></li>
                <li><a href="/exam" className="text-slate-400 hover:text-white transition-colors">Practice Exams</a></li>
                <li><a href="/analytics" className="text-slate-400 hover:text-white transition-colors">Progress Tracking</a></li>
                <li><a href="/ai" className="text-slate-400 hover:text-white transition-colors">AI Assistant</a></li>
              </ul>
            </div>

            {/* Resources Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/research" className="text-slate-400 hover:text-white transition-colors">Research</a></li>
                <li><a href="/jobs" className="text-slate-400 hover:text-white transition-colors">Jobs</a></li>
                <li><a href="/import" className="text-slate-400 hover:text-white transition-colors">Import Questions</a></li>
                <li><a href="/contact" className="text-slate-400 hover:text-white transition-colors">Help Center</a></li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">About Us</a></li>
                <li><a href="/contact" className="text-slate-400 hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              &copy; 2025 {tenantName}. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm">
              Made with care in Australia
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactPage;
